import { useMarkets } from '@api/useMarkets'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { GraviButton, GraviGrid, Texto, validateFloat, validateInt } from '@gravitate-js/excalibrr'
import { useApi } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import api, { getAPIURL } from '@utils/api'
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { SectorGrid } from '../Sector/SectorGrid'
import { marketCreateConfig } from './createConfig'
import { Popover, Tooltip, TreeSelect } from 'antd'
import { InNetworkTreeSelectEditor } from '@components/_SiteWide/Grid/CellEditors/InNetworkTerminalsTreeSelect'
import { InNetworkTermialsCellRenderer } from '@components/_SiteWide/Grid/CellRenderers/InNetworkTermialsCellRenderer'
import { gridViewManager } from '@/utils/grid'
const { SHOW_PARENT } = TreeSelect

const STORAGE_KEY = 'market'

export function Market() {
  const { useMarketsQuery, createMarketMutation, updateMarketMutation, useMarketMetadataQuery } = useMarkets()
  const { data: marketData, isPending: marketsLoading, refetch } = useMarketsQuery()
  const apiHook = useApi({ baseURLOverride: getAPIURL() })

  const { data: metadata } = useMarketMetadataQuery()
  const { updateSetting } = useUserSettings()
  const gridApiRef = useRef()

  const createMarketWrapper = (values) => {
    const prom = createMarketMutation.mutate(values)
    const res = new Promise((resolve) => {
      resolve({ data: prom })
    })
    return res
  }
  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.location_write
  const excelOptions = [
    {
      title: 'Download Markets',
      downloadEP: api.exportMarkets,
      fileName: 'Markets.xlsx',
    },
    {
      title: 'Upload Markets',
      uploadEP: (file) =>
        apiHook.uploadFile('market/import', file, {
          headers: { Accept: 'application/json' },
        }),
    },
    {
      title: 'Download Sectors',
      downloadEP: api.exportSectors,
      fileName: 'Sectors.xlsx',
    },
    {
      title: 'Upload Sectors',
      uploadEP: api.importSectors,
    },
  ]
  const filteredOptions = excelOptions.filter((option) => {
    if (option.title.includes('Download') || canEdit) {
      return true
    }

    return false
  })
  const treeData = useMemo(() => {
    if (!metadata?.supply_zones) return []
    const supplyZonesArray = Object.entries(metadata?.supply_zones)
    return supplyZonesArray.map(([parent, children]) => {
      return {
        title: parent,
        value: parent,
        key: parent,
        children: children.map((child) => ({ title: child, value: child, key: child })),
      }
    })
  }, [metadata?.supply_zones])

  const columnDefs = useMemo(() => {
    if (!metadata) return []
    metadata.delivery_window_defaults.push({ name: 'None', _id: null })
    return getColumnDefs(metadata, treeData)
  }, [metadata, treeData])
  const getRowId = useCallback((row) => row?.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
    }),
    [getRowId]
  )
  const handleUpdate = async (values) => {
    if (values.delivery_window_default_name) {
      values.delivery_window_defaults = undefined
      if (values.delivery_window_default_name === 'None') {
        values.delivery_window_default_id = null
        values.delivery_window_default_name = null
      } else {
        const config = metadata.delivery_window_defaults.find(
          (item) => item.name === values.delivery_window_default_name
        )
        values.delivery_window_default_id = config._id
      }
    }
    if (values.default_transporter) {
      values.default_transporter_id = null
    }
    if (values.in_network_terminal_names) {
      values.in_network_terminals = values.in_network_terminal_names
    }

    const res = await updateMarketMutation.mutateAsync(values)

    values.only_in_network_terminal_names = res.only_in_network_terminal_names
    values.in_network_supply_zone_names = res.in_network_supply_zone_names

    const node = gridApiRef?.current?.getRowNode(res.id)
    gridApiRef?.current?.refreshCells({ rowNodes: [node], force: true })

    return res
  }
  return (
    <GraviGrid
      controlBarProps={{
        title: 'Market Management',
        actionButtons: <UploadDownloadButtons refreshCallback={refetch} excelOptions={filteredOptions} />,
      }}
      masterDetail
      detailCellRenderer={SectorGrid}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      detailCellRendererParams={{
        metadata,
      }}
      shouldInsertCreated
      storageKey={STORAGE_KEY}
      rowData={marketData?.rows}
      loading={marketsLoading}
      updateEP={canEdit && handleUpdate}
      createEP={canEdit && createMarketWrapper}
      createConfig={marketCreateConfig}
      createSelectOptions={metadata}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      toolPanelWidth={270}
      externalRef={gridApiRef}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

function getFilterOptionsForTerminals(marketMetadata, params) {
  const flat_list = []
  Object.keys(marketMetadata?.supply_zones)?.forEach((zone) => {
    marketMetadata?.supply_zones[zone].forEach((terminal) => {
      flat_list.push(terminal)
    })
  })
  return params.success(flat_list)
}

function getFilterOptionsForSupplyZones(marketMetadata, params) {
  const flat_list = []
  Object.keys(marketMetadata?.supply_zones)?.forEach((zone) => {
    if (zone === 'No Supply Zone') return
    flat_list.push(zone)
  })
  return params.success(flat_list)
}

function customValueFormatter(params) {
  return params.value
}

const getColumnDefs = (metadata, treeData) => [
  {
    headerName: 'Name',
    field: 'name',
    cellRenderer: 'agGroupCellRenderer',
  },
  {
    headerName: 'Active',
    field: 'active',
    cellEditor: 'agSelectCellEditor',
    valueGetter: (params) => (params.data?.active ? 'Yes' : 'No'),
    valueSetter: (params) => {
      params.data.active = params.newValue === 'Yes'
    },
    cellEditorParams: {
      values: ['Yes', 'No'],
    },
  },
  {
    headerName: 'Network Radius (miles)',
    field: 'network_radius',
    valueSetter: validateInt,
    valueGetter: (params) => {
      return params.data.network_radius
    },
    valueFormatter: (params) => {
      if (params.data.in_network_terminal_names?.length) {
        return (
          <Tooltip title={`Overridden By In Network Terminals`}>
            <GraviButton
              className={'ghost-gravi-button p-0'}
              style={{ color: 'var(--gray-400)', textAlign: 'left', fontWeight: 'normal' }}
              buttonText={params.data.network_radius}
            />
          </Tooltip>
        )
      }
      return <Texto>{params.data.network_radius}</Texto>
    },
    cellRenderer: (params) => {
      if (params.data.in_network_terminal_names?.length) {
        return (
          <Tooltip title={`Overridden By In Network Terminals`}>
            <GraviButton
              className={'ghost-gravi-button p-0'}
              style={{ color: 'var(--gray-400)', textAlign: 'left', fontWeight: 'normal' }}
              buttonText={params.data.network_radius}
            />
          </Tooltip>
        )
      }
      return <Texto>{params.data.network_radius}</Texto>
    },
  },
  {
    headerName: 'Unloaded Rate',
    field: 'unloaded_rate',
    valueSetter: validateFloat,
    rules: [
      {
        required: true,
        message: 'Value required',
      },
    ],
  },
  {
    headerName: 'Trailer Config',
    field: 'trailer_config',
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: {
      values: metadata.trailer_configs,
    },
    rules: [
      {
        required: true,
        message: 'Trailer configuration required',
      },
    ],
  },
  {
    headerName: 'Default Transporter',
    headerTooltip: 'Used as the carrier to match freight rates for internal orders',
    field: 'default_transporter',
    cellEditor: 'agRichSelectCellEditor',
    cellEditorParams: {
      values: metadata.counterparty_names,
    },
    rules: [
      {
        required: false,
      },
    ],
  },
  {
    headerName: 'Units',
    editable: false,
    field: 'profile.name',
    cellEditor: 'agSelectCellEditor',
  },
  {
    headerName: 'Order Request Window Configuration',
    field: 'delivery_window_default_name',
    valueGetter: (params) =>
      params.data?.delivery_window_default_name === 'None' ? null : params.data?.delivery_window_default_name,
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: {
      values: metadata?.delivery_window_defaults?.map((item) => item?.name),
    },
  },
  {
    headerName: 'In-Network Terminals',
    field: 'in_network_terminal_names',
    cellEditor: InNetworkTreeSelectEditor,
    cellEditorParams: {
      treeData,
      supply_zones: metadata?.supply_zones,
    },
    cellRenderer: InNetworkTermialsCellRenderer,
    minWidth: 200,
    valueFormatter: (params) => {
      if (params.node?.group) {
        const node = params?.node?.childrenAfterGroup[0]
        const terminals = node?.data?.only_in_network_terminal_names.join(', ')
        const supplyZones = node?.data?.in_network_supply_zone_names.join(', ')

        if (!terminals && !supplyZones) {
          return 'No In Network Terminals'
        }

        if (!terminals) {
          return supplyZones
        }

        if (!supplyZones) {
          return terminals
        }

        return `${supplyZones} --- ${terminals}`
      }

      return params.value
    },
    filter: 'agMultiColumnFilter',
    filterParams: {
      filters: [
        {
          filter: 'agSetColumnFilter',
          display: 'subMenu',
          title: 'Filter By Supply Zone',
          filterParams: {
            field: 'number',
            buttons: ['reset'],
            values: (params) => getFilterOptionsForSupplyZones(metadata, params),
            valueGetter: (params) => {
              return params?.data?.in_network_supply_zone_names
            },
            valueFormatter: customValueFormatter,
          },
        },
        {
          filter: 'agSetColumnFilter',
          display: 'subMenu',
          title: 'Filter By Terminal',
          filterParams: {
            showTooltips: true,
            field: 'type',
            values: (params) => getFilterOptionsForTerminals(metadata, params),
            valueGetter: ({ data }) => {
              return data?.in_network_terminal_names
            },
            valueFormatter: customValueFormatter,
            buttons: ['reset'],
          },
        },
      ],
    },
  },
  {
    headerName: 'In-Network By Market',
    field: 'in_network_by_market',
    cellEditor: 'agSelectCellEditor',
    headerTooltip:
      'Will further restrict the in-network terminals to those that are in the same market as the site after filtering by selection or radius',
    valueGetter: (params) => (params.data?.in_network_by_market ? 'Yes' : 'No'),
    valueSetter: (params) => {
      params.data.in_network_by_market = params.newValue === 'Yes'
    },
    cellEditorParams: {
      values: ['Yes', 'No'],
    },
  },
]
